import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../../Services/api';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    mobileNumber: '',
    countryCode: '+44',
    password: '',
    passwordConfirm: '',
    address: '',
    latitude: '',
    longitude: '',
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Address autocomplete
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Carousel slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // AOS animations
  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 120 });
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchSuggestions = useCallback((query) => {
    if (query.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=gb&limit=6`,
      { headers: { 'Accept-Language': 'en' } }
    )
      .then((r) => r.json())
      .then((results) => {
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      })
      .catch(() => setSuggestions([]));
  }, []);

  const handleAddressChange = (e) => {
    const val = e.target.value;
    setAddressQuery(val);
    setAddressSelected(false);
    setFormData((prev) => ({ ...prev, address: val, latitude: '', longitude: '' }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handleSuggestionClick = (s) => {
    const display = s.display_name;
    setAddressQuery(display);
    setFormData((prev) => ({
      ...prev,
      address: display,
      latitude: parseFloat(s.lat),
      longitude: parseFloat(s.lon),
    }));
    setAddressSelected(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const cleanMobile = (formData.mobileNumber || '').replace(/\D/g, '');
      const { data } = await API.post('/users/register', { ...formData, mobileNumber: cleanMobile });
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('role', data.role || 'user');
        if (data.userId) localStorage.setItem('userId', data.userId);
      }
      setSuccess('Account created successfully!');
      const pendingRecycling = sessionStorage.getItem('pendingRecyclingState');
      const fromRecycling = new URLSearchParams(window.location.search).get('from') === 'recycling';
      setTimeout(() => {
        if (pendingRecycling && fromRecycling) {
          window.location.href = '/map?restore=recycling';
        } else {
          window.location.href = '/profile';
        }
      }, 2000);
    } catch (err) {
      let msg = err.response?.data?.error || err.response?.data?.message || '';
      // Parse ugly Mongoose duplicate key errors into clean messages
      if (!msg || msg.includes('E11000') || msg.includes('duplicate key')) {
        if (msg.includes('email')) msg = 'This email address is already registered. Please log in or use a different email.';
        else if (msg.includes('mobileNumber') || msg.includes('mobile')) msg = 'This mobile number is already registered. Please use a different number.';
        else if (msg) msg = 'This account already exists. Please try logging in.';
        else msg = 'An error occurred. Please check your details and try again.';
      }
      if (msg.includes('valid mobile')) msg = 'Please enter a valid mobile number (digits only, e.g. 07700900123).';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-gray-100 font-Inter py-4 sm:py-10 px-3 sm:px-6 md:px-12 min-h-screen flex flex-col items-center justify-center">
        <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-stretch overflow-hidden bg-white rounded-3xl shadow-sm">

          {/* ── FORM ── */}
          <div className="p-6 sm:p-8 w-full md:w-5/12 lg:w-1/3" data-aos="fade-right" data-aos-delay="100">
            <div className="flex items-center justify-center mb-6">
              <a href="/"><img src="/images/Second_logo.png" alt="e-save Logo" className="h-10" /></a>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-2">Create your account</h2>
            <p className="text-gray-600 text-center mb-4 sm:mb-6 text-sm sm:text-base">Join e-save to start your recycle journey.</p>

            {error && <div className="text-red-500 text-center mb-4 text-sm">{error}</div>}

            {success && (
              <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
                <div className="bg-white rounded-2xl shadow-2xl p-8 text-center w-72 space-y-4 animate-[fadeIn_0.3s_ease]">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-[#29752B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#172A3A]">Welcome to e-save! 🎉</h3>
                  <p className="text-gray-600 text-sm">
                    {sessionStorage.getItem('pendingRecyclingState') && new URLSearchParams(window.location.search).get('from') === 'recycling'
                      ? 'Your account is ready. Taking you back to complete your recycling request…'
                      : 'Your account is ready. Taking you to your dashboard…'}
                  </p>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-[#29752B] h-1.5 rounded-full animate-[progress_2s_linear]" style={{ width: '100%', animation: 'width 2s linear' }} />
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div className="relative">
                <img src="/images/Username_icon.png" alt="" className="absolute top-1/2 left-4 -translate-y-1/2 h-[24px] w-[20px]" />
                <input
                  type="text" name="username" placeholder="Full Name" required
                  value={formData.username} onChange={handleChange}
                  className="w-full h-[45px] border border-gray-400 pl-12 pr-4 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <img src="/images/Mail_icon.png" alt="" className="absolute top-1/2 left-4 -translate-y-1/2 h-[17px] w-[20px]" />
                <input
                  type="email" name="email" placeholder="Email e.g. name@mail.com" required
                  value={formData.email} onChange={handleChange}
                  className="w-full h-[45px] border border-gray-400 pl-12 pr-4 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                />
              </div>

              {/* Mobile */}
              <div className="flex space-x-2">
                <select
                  name="countryCode" value={formData.countryCode} onChange={handleChange}
                  className="w-1/4 h-[45px] border border-gray-400 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                >
                  <option value="+44">+44</option>
                  <option value="+1">+1</option>
                  <option value="+91">+91</option>
                </select>
                <input
                  type="tel" name="mobileNumber" placeholder="Mobile number e.g. 07700900123" required
                  value={formData.mobileNumber} onChange={handleChange}
                  className="w-3/4 h-[45px] border border-gray-400 pl-3 pr-4 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                />
              </div>

              {/* Address autocomplete */}
              <div className="relative" ref={suggestionsRef}>
                <img src="/images/address_icon.png" alt="" className="absolute top-3.5 left-4 h-[21px] w-[20px] z-10" />
                <input
                  type="text"
                  placeholder="Start typing your UK address…"
                  value={addressQuery}
                  onChange={handleAddressChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  autoComplete="off"
                  className={`w-full h-[45px] border pl-12 pr-4 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] ${addressSelected ? 'border-[#29752B] bg-green-50' : 'border-gray-400'}`}
                />
                {/* Suggestions dropdown */}
                {showSuggestions && (
                  <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-56 overflow-y-auto text-sm">
                    {suggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => handleSuggestionClick(s)}
                        className="px-4 py-2.5 cursor-pointer hover:bg-[#F0FBF4] border-b border-gray-100 last:border-0 leading-snug"
                      >
                        <span className="font-medium text-gray-800">
                          {s.address?.road || s.address?.hamlet || s.name}
                        </span>
                        <span className="text-gray-500 text-xs block truncate">{s.display_name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {addressQuery.length >= 3 && !showSuggestions && !addressSelected && suggestions.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1 pl-1">
                    No UK addresses found — you can still register and update your address later.
                  </p>
                )}
                {addressSelected && (
                  <p className="text-xs text-[#29752B] mt-1 pl-1">✓ Address confirmed</p>
                )}
              </div>

              {/* Password */}
              <div className="relative">
                <img src="/images/Password_icon.png" alt="" className="absolute top-1/2 left-4 -translate-y-1/2 h-[20px] w-[20px]" />
                <input
                  type={showPassword ? 'text' : 'password'} name="password" placeholder="Password" required
                  value={formData.password} onChange={handleChange}
                  className="w-full h-[45px] border border-gray-400 pl-12 pr-4 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                />
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <img src="/images/Password_icon.png" alt="" className="absolute top-1/2 left-4 -translate-y-1/2 h-[20px] w-[20px]" />
                <input
                  type={showPassword ? 'text' : 'password'} name="passwordConfirm" placeholder="Confirm Password" required
                  value={formData.passwordConfirm} onChange={handleChange}
                  className="w-full h-[45px] border border-gray-400 pl-12 pr-4 rounded-lg focus:outline-none focus:border-[#29752B] focus:ring-1 focus:ring-[#29752B]"
                />
              </div>

              {/* Show password */}
              <div className="flex items-center">
                <input
                  type="checkbox" id="showPassword" checked={showPassword}
                  onChange={() => setShowPassword((p) => !p)}
                  className="mr-2 h-4 w-4 border-2 border-gray-400 rounded cursor-pointer checked:bg-[#29752B] checked:border-[#29752B]"
                />
                <label htmlFor="showPassword" className="text-gray-600 text-sm cursor-pointer">Show Password</label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#29752B] text-white font-medium py-3 rounded-lg hover:bg-[#004d3f] focus:outline-none disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Creating account…' : 'Sign up'}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
                <div className="relative flex justify-center text-sm"><span className="bg-white px-2 text-gray-500 z-10">or</span></div>
              </div>

              <div className="text-center">
                <p className="text-gray-800">
                  Already have an account?{' '}
                  <a href="/login" className="text-[#29752B] hover:underline">Sign in</a>
                </p>
              </div>
            </form>
          </div>

          {/* ── IMAGE CAROUSEL ── */}
          <div className="hidden md:flex md:w-7/12 lg:w-2/3 relative" data-aos="fade-left" data-aos-delay="200">
            <div className="w-full min-h-[400px] md:min-h-full relative overflow-hidden rounded-r-3xl bg-[#f3f4f6]">
              <div className="md:w-[455px] md:h-[302px] overflow-hidden mx-auto mb-6 mt-3 md:mt-20">
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                  <img src="/images/img_carousel1.png" alt="Carousel 1" className="w-full h-full object-cover shrink-0" />
                  <img src="/images/img_carousel2.png" alt="Carousel 2" className="w-full h-full object-cover shrink-0" />
                  <img src="/images/img_carousel3.png" alt="Carousel 3" className="w-full h-full object-cover shrink-0" />
                </div>
              </div>
              <div className="md:absolute top-3/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-4">Recycle, Track and Earn</h3>
                <p className="text-black mb-6">Locate nearby e-waste centres</p>
                <div className="flex justify-center space-x-3">
                  {[0, 1, 2].map((dot) => (
                    <span key={dot} className={`h-2 w-2 rounded-full inline-block ${dot === currentSlide ? 'bg-gray-800' : 'bg-gray-400'}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
    </section>
  );
};

export default Register;
